import axios from 'axios';

const API_BASE = '/api';

export interface MeDomain {
  id: number;
  slug: string;
  name_en: string;
}

export interface Me {
  id: number;
  username: string;
  is_superuser: boolean;
  domains: MeDomain[];
}

const ME_CACHE_KEY = 'me';

// Global 401 interceptor — auto-redirect to login on expired token. Also
// clears the cached profile so the UI doesn't keep showing stale data.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/admin/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem(ME_CACHE_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const login = async (username: string, password: string) => {
  const response = await axios.post(`${API_BASE}/admin/login`, {
    username,
    password
  });

  const token = response.data.access_token;
  localStorage.setItem('token', token);
  // Eagerly fetch /admin/me so the sidebar/route guards see profile info
  // on the first render without an extra round-trip from each consumer.
  try {
    const meResp = await axios.get(`${API_BASE}/admin/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    localStorage.setItem(ME_CACHE_KEY, JSON.stringify(meResp.data));
  } catch {
    // Non-fatal: pages that need /me will re-fetch via fetchMe()
  }
  return token;
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem(ME_CACHE_KEY);
};

export const getToken = () => {
  return localStorage.getItem('token');
};

/**
 * Client-side session validity: the token must exist AND its JWT `exp` must be
 * in the future. Keeps an expired session from ever rendering the admin shell
 * (the 401 axios interceptor is the server-side backstop). A token that can't
 * be decoded is treated as invalid.
 */
export const isSessionValid = (): boolean => {
  const token = getToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 > Date.now() + 5000; // 5s clock skew
  } catch {
    return false;
  }
};

export const getAuthHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getMe = (): Me | null => {
  const raw = localStorage.getItem(ME_CACHE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as Me; } catch { return null; }
};

/**
 * Re-fetches /admin/me from the server and updates the cached profile.
 * Call this after permission-affecting mutations (creating a user, granting
 * a domain) so subsequent renders see the truth.
 */
export const fetchMe = async (): Promise<Me | null> => {
  try {
    const resp = await axios.get(`${API_BASE}/admin/me`, { headers: getAuthHeaders() });
    localStorage.setItem(ME_CACHE_KEY, JSON.stringify(resp.data));
    return resp.data as Me;
  } catch {
    return null;
  }
};

export const isSuperuser = (): boolean => !!getMe()?.is_superuser;
